import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Clock } from "lucide-react";

import { normalizeReportsMine, normalizeDecisions, decisionOptions, appealErrors, reportLine, dateDe, APPEAL_MAX } from "@/lib/v3Settings";
import { useCore } from "../useCore";
import { ConnectionError } from "../components/ConnectionError";
import { Loading } from "../components/kit";
import { V3Title, V3Columns, V3Actions, AsideCard, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { V3TextArea, SectionTitle } from "../components/v3app";
import { InfoBox } from "../components/v3people";
import { Blocked, RowSelect } from "../components/v3plans";

const Aside = ({ body }) => (<>
  <AsideCard icon={MessageSquare} title="Nachrichtenvorschau" text={body.trim() || "Noch kein Text"} testid="appeal-preview" />
  <AsideCard icon={Clock} tone="white" title="Versandstatus" text="Entwurf · Noch nicht gesendet" testid="appeal-status-card" />
</>);

// M113: decisions come from GET /member/reports/mine (Core 1.1) and, proposed, GET /member/moderation/decisions (D-92);
// sending the objection needs POST /member/moderation/decisions/{id}/appeal (D-92), so it stays locked.
function AppealForm({ reports, decisions }) {
  const options = decisionOptions(decisions, reports);
  const [decision, setDecision] = useState(options[0]?.id || "");
  const [body, setBody] = useState("");
  const [errors, setErrors] = useState({});
  const [review, setReview] = useState(false);
  const cur = options.find((o) => o.id === decision) || null;
  const check = () => { const e = appealErrors({ decision, body }, options); setErrors(e); setReview(!Object.keys(e).length); };
  return (
    <V3Columns layout="detail" aside={<Aside body={body} />} main={<div data-m="M113">
      <V3TextArea id="appeal-body" label="Dein Text" value={body} onChange={(e) => { setBody(e.target.value); setReview(false); }} max={APPEAL_MAX} error={errors.body}
        placeholder="Zum Beispiel: Ich möchte den Zusammenhang ergänzen." hint="Nur Text. Bitte sachlich und ohne Angaben über andere Personen." />
      <DetailList>
        {options.length ? (
          <RowSelect id="appeal-decision" label="Entscheidung" value={decision} onChange={(v) => { setDecision(v); setReview(false); }} error={errors.decision} testid="appeal-decision">
            {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </RowSelect>
        ) : <DetailRow label="Entscheidung" value="Keine Entscheidung zum Prüfen vorhanden" testid="appeal-decision-none" />}
        <DetailRow label="Anhang" value="Freiwillig · Nur notwendiger Kontext · Anhänge noch nicht verfügbar" testid="appeal-attachment" />
        <DetailRow label="Status" value="Anfrage noch nicht gesendet" testid="appeal-state" />
      </DetailList>
      {decisions === null && <Blocked id="appeal-decisions-unavailable">Entscheidungen zu deinem eigenen Konto (z. B. eine Schreibbeschränkung) liefert der gemeinsame Core noch nicht. Angezeigt werden nur Entscheidungen zu deinen Meldungen.</Blocked>}
      {!options.length && <p className="mt-[11px] text-[13px] text-v3-muted" data-testid="appeal-none-note">Sobald zu einer deiner Meldungen entschieden wurde, kannst du sie hier zur erneuten Prüfung auswählen.</p>}
      {review && <div className="mt-[11px]"><InfoBox title="Einwand prüfen" testid="appeal-review" text={`Zu: ${cur.label}. Übermittelt würde nur dein Text, ohne weitere Inhalte.`} /></div>}
      {review && <Blocked id="appeal-blocked">Einwände sind im gemeinsamen Core noch nicht eingerichtet. Es wird nichts gesendet; die Entscheidung bleibt unverändert.</Blocked>}
      <V3Actions>
        {review ? <button type="button" disabled aria-describedby="appeal-blocked" data-testid="appeal-send" className={V3_PRIMARY}>Einwand senden</button>
          : <button type="button" onClick={check} disabled={!options.length} data-testid="appeal-check" className={V3_PRIMARY}>Einwand prüfen</button>}
        {review ? <button type="button" onClick={() => setReview(false)} data-testid="appeal-edit" className={V3_SECONDARY}>Ändern</button>
          : <Link to="/hilfe" data-testid="appeal-cancel" className={V3_SECONDARY}>Abbrechen</Link>}
      </V3Actions>
      <div className="mt-[30px]"><SectionTitle id="appeal-reports">Meine Meldungen</SectionTitle></div>
      <div className="mt-[14px]" aria-labelledby="appeal-reports" role="region">
        {reports.length ? <DetailList>{reports.map((r) => (
          <DetailRow key={r.id} label={r.createdAt ? `Meldung vom ${dateDe(r.createdAt)}` : "Meldung"} value={reportLine(r)} testid={`appeal-report-${r.id}`} />
        ))}</DetailList> : <p className="text-[14px] text-v3-muted" data-testid="appeal-reports-empty">Du hast noch nichts gemeldet.</p>}
      </div>
    </div>} />
  );
}

export default function Moderation() {
  const reports = useCore("/member/reports/mine", (d) => ({ list: normalizeReportsMine(d) }));
  const decisions = useCore("/member/moderation/decisions", (d) => ({ list: normalizeDecisions(d) }));
  const decList = decisions.error === "notfound" ? null : decisions.data ? decisions.data.list : decisions.error ? null : undefined;
  return (
    <div data-testid="appeal-page">
      <V3Title title="Anfrage zu einer Moderationsentscheidung" intro="Bitte um eine erneute Prüfung und beschreibe deinen Einwand sachlich." testid="appeal-title" />
      {reports.error ? <ConnectionError onRetry={() => { reports.reload(); decisions.reload(); }} />
        : !reports.data || decList === undefined ? <Loading />
        : <AppealForm reports={reports.data.list || []} decisions={decList} />}
    </div>
  );
}
